import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import data from '../data/data.json';
import MagneticButton from './MagneticButton';

const CAT_COLORS = {
  'HealthTech Analytics': { bg: 'rgba(58, 112, 80, 0.12)', color: '#3a7050', border: 'rgba(58, 112, 80, 0.25)' },
  'AI & ML': { bg: 'rgba(232, 149, 109, 0.16)', color: '#c97348', border: 'rgba(232, 149, 109, 0.3)' },
  'Product Engineering': { bg: 'rgba(247, 190, 161, 0.25)', color: '#a06040', border: 'rgba(247, 190, 161, 0.4)' },
  'Full Stack': { bg: 'rgba(45, 23, 83, 0.1)', color: '#2D1753', border: 'rgba(45, 23, 83, 0.2)' },
  'Case Studies': { bg: 'rgba(80, 64, 160, 0.12)', color: '#5040a0', border: 'rgba(80, 64, 160, 0.25)' },
};

function GenerativeArtwork({ category, title }) {
  const seed = title.length;

  if (category === 'AI & ML') {
    return (
      <svg viewBox="0 0 400 220" style={{ width: '100%', height: '100%' }}>
        <defs>
          <radialGradient id={`aiml-grad-${seed}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#E8956D" stopOpacity="0.35" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <rect width="100%" height="100%" fill={`url(#aiml-grad-${seed})`} />
        {[
          [60, 60], [60, 110], [60, 160],
          [160, 40], [160, 90], [160, 130], [160, 180],
          [260, 70], [260, 120], [260, 160],
          [340, 110]
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="5" fill="#E8956D" opacity="0.8" />
            <circle cx={x} cy={y} r="10" fill="#E8956D" opacity="0.2" />
          </g>
        ))}
        <path
          d="M60,60 L160,40 M60,60 L160,90 M60,110 L160,90 M60,110 L160,130 M60,160 L160,130 M60,160 L160,180
             M160,40 L260,70 M160,90 L260,70 M160,90 L260,120 M160,130 L260,120 M160,130 L260,160 M160,180 L260,160
             M260,70 L340,110 M260,120 L340,110 M260,160 L340,110"
          stroke="#E8956D"
          strokeWidth="1.2"
          opacity="0.3"
          strokeDasharray="4 3"
        />
      </svg>
    );
  }

  if (category === 'HealthTech Analytics' || category === 'Product Engineering') {
    return (
      <svg viewBox="0 0 400 220" style={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={`ht-grad-${seed}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(58, 112, 80, 0.2)" />
            <stop offset="100%" stopColor="rgba(247, 190, 161, 0.15)" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill={`url(#ht-grad-${seed})`} />
        <polyline
          points="20,110 80,110 110,110 130,50 145,170 160,90 175,125 190,110 240,110 260,80 275,140 290,110 380,110"
          fill="none"
          stroke="#3a7050"
          strokeWidth="2.2"
          opacity="0.55"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {[-30, 0, 30].map((dy, i) => (
          <line key={i} x1="20" y1={110 + dy} x2="380" y2={110 + dy} stroke="#3a7050" strokeWidth="0.5" strokeDasharray="3 4" opacity="0.2" />
        ))}
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 400 220" style={{ width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id={`fs-grad-${seed}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(45, 23, 83, 0.15)" />
          <stop offset="100%" stopColor="rgba(232, 149, 109, 0.12)" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill={`url(#fs-grad-${seed})`} />
      <rect x="70" y="55" width="100" height="40" rx="8" fill="rgba(45, 23, 83, 0.18)" stroke="#2D1753" strokeWidth="1.2" opacity="0.6" />
      <rect x="230" y="55" width="100" height="40" rx="8" fill="rgba(45, 23, 83, 0.18)" stroke="#2D1753" strokeWidth="1.2" opacity="0.6" />
      <rect x="150" y="135" width="100" height="40" rx="8" fill="rgba(232, 149, 109, 0.25)" stroke="#E8956D" strokeWidth="1.2" opacity="0.8" />
      <path d="M120,95 L120,115 L200,115 L200,135 M280,95 L280,115 L200,115" fill="none" stroke="#2D1753" strokeWidth="1.2" opacity="0.4" strokeDasharray="4 3" />
    </svg>
  );
}

function ProjectCard({ project, index }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50 });
  const catStyle = CAT_COLORS[project.category] || { bg: 'rgba(45,23,83,0.08)', color: '#2D1753', border: 'rgba(45,23,83,0.15)' };

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
    const y = -((e.clientX - rect.left) / rect.width - 0.5) * 10;
    setTilt({ x, y });
    setGlarePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.55, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      style={{
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: 'transform 0.15s ease-out',
        height: '100%',
      }}
    >
      <Link
        to={`/projects/${project.id}`}
        data-cursor-hover
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: 'var(--bg-card-glass)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid var(--border)',
          borderRadius: '22px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-sm)',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--accent)';
          e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Specular Glare Effect */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at ${glarePos.x}% ${glarePos.y}%, rgba(255, 255, 255, 0.4) 0%, transparent 60%)`,
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />

        {/* Card Visual with Optical Fluted Glass Filter */}
        <div
          className="fluted-glass-container"
          style={{
            height: '210px',
            position: 'relative',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
          }}
        >
          {/* Fluted Glass Optical Texture Overlay */}
          <div className="fluted-glass-overlay" />

          {project.thumbnail ? (
            <img
              src={project.thumbnail}
              alt={project.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.5s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            />
          ) : (
            <GenerativeArtwork category={project.category} title={project.title} />
          )}

          {/* Category Pill Tag */}
          <span
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              padding: '0.35rem 0.85rem',
              borderRadius: '100px',
              background: 'var(--bg-glass)',
              color: catStyle.color,
              backdropFilter: 'blur(8px)',
              border: `1px solid ${catStyle.border}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              zIndex: 5,
            }}
          >
            {project.category}
          </span>
        </div>

        {/* Card Body */}
        <div
          style={{
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.22rem',
                fontWeight: 600,
                color: 'var(--text)',
                lineHeight: 1.3,
                marginBottom: '0.65rem',
              }}
            >
              {project.title}
            </h3>

            <p
              style={{
                fontSize: '0.9rem',
                color: 'var(--text-muted)',
                marginBottom: '1.4rem',
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.tagline}
            </p>
          </div>

          <div>
            {/* Tech Tags */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.4rem',
                marginBottom: '1.4rem',
              }}
            >
              {project.tech.slice(0, 4).map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.7rem',
                    borderRadius: '100px',
                    background: 'rgba(45, 23, 83, 0.04)',
                    border: '1px solid var(--border)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {t}
                </span>
              ))}
              {project.tech.length > 4 && (
                <span
                  style={{
                    fontSize: '0.72rem',
                    padding: '0.25rem 0.5rem',
                    color: 'var(--text-faint)',
                  }}
                >
                  +{project.tech.length - 4}
                </span>
              )}
            </div>

            {/* View Project Link CTA */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--accent-dark)',
              }}
            >
              <span>Explore Case Study</span>
              <span style={{ transition: 'transform 0.2s ease', display: 'inline-block' }}>→</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const projects = data.projects;

  const categoriesWithCounts = useMemo(() => {
    const counts = { All: projects.length };
    projects.forEach((p) => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return Object.entries(counts);
  }, [projects]);

  const featuredProject = useMemo(() => {
    return projects.find((p) => p.featured) || projects[0];
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
      const matchesSearch =
        searchQuery.trim() === '' ||
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tagline.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.tech.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [projects, activeCategory, searchQuery]);

  const handleCategorySelect = (cat) => {
    setActiveCategory(cat);
  };

  return (
    <section id="projects" className="section">
      <style>{`
        .featured-banner {
          display: grid;
          grid-template-columns: 1.15fr 0.85fr;
          gap: 3rem;
          align-items: center;
          background: var(--bg-card-glass);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--border-accent);
          border-radius: 28px;
          padding: 3rem;
          margin-bottom: 4.5rem;
          box-shadow: var(--shadow-md);
          position: relative;
          overflow: hidden;
        }

        .featured-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          margin: 1.8rem 0;
          padding: 1.25rem;
          background: var(--bg-glass);
          border-radius: 16px;
          border: 1px solid var(--border);
        }

        @media (max-width: 960px) {
          .featured-banner {
            grid-template-columns: 1fr;
            padding: 2rem;
            gap: 2rem;
          }
          .featured-metrics {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 600px) {
          .featured-metrics {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="container">
        {/* Section Header with Foil Shimmer */}
        <motion.p
          className="section-label"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Selected Works & Prototypes
        </motion.p>
        <motion.h2
          className="section-title foil-shine"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Featured Case Studies
        </motion.h2>

        {/* Primary Featured Project Spotlight */}
        {featuredProject && (
          <motion.div
            className="featured-banner"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Left Content */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.85rem' }}>
                <span
                  style={{
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '100px',
                    background: 'var(--accent)',
                    color: '#fff',
                  }}
                >
                  Spotlight Case Study
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {featuredProject.category}
                </span>
              </div>

              <h3
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'clamp(1.6rem, 3vw, 2.3rem)',
                  fontWeight: 700,
                  color: 'var(--text)',
                  lineHeight: 1.2,
                  marginBottom: '1rem',
                }}
              >
                {featuredProject.title}
              </h3>

              <p style={{ fontSize: '1rem', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                {featuredProject.tagline}
              </p>

              {/* Metric Highlights */}
              <div className="featured-metrics">
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', fontWeight: 700, color: 'var(--accent-dark)' }}>
                    150K+
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Hospital Events</div>
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', fontWeight: 700, color: '#3a7050' }}>
                    82 Tests
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Pipeline Verification</div>
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' }}>
                    F = (W+R+D)/3
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Composite Score</div>
                </div>
              </div>

              {/* Action Buttons with Magnetic Physics */}
              <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <MagneticButton strength={0.3}>
                  <Link
                    to={`/projects/${featuredProject.id}`}
                    className="btn btn-primary"
                    data-cursor-hover
                  >
                    Read Full Case Study ↗
                  </Link>
                </MagneticButton>

                {featuredProject.github && (
                  <MagneticButton strength={0.3}>
                    <a
                      href={featuredProject.github}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline"
                      data-cursor-hover
                    >
                      GitHub Repository
                    </a>
                  </MagneticButton>
                )}
              </div>
            </div>

            {/* Right Preview Image with Fluted Glass Optical Overlay */}
            <div
              className="fluted-glass-container"
              style={{
                borderRadius: '20px',
                boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--border)',
                background: 'var(--bg-card)',
                aspectRatio: '16 / 10',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div className="fluted-glass-overlay" />
              <img
                src={featuredProject.thumbnail || '/images/projects/HealthcareProcessMiningPage1.png'}
                alt={featuredProject.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          </motion.div>
        )}

        {/* Filter and Search Bar Row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap',
            marginBottom: '3rem',
          }}
        >
          {/* Category Filter Pills */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {categoriesWithCounts.map(([cat, count]) => {
              const isActive = activeCategory === cat;
              return (
                <MagneticButton key={cat} strength={0.2}>
                  <button
                    onClick={() => handleCategorySelect(cat)}
                    data-cursor-hover
                    style={{
                      padding: '0.45rem 1.15rem',
                      borderRadius: '100px',
                      fontSize: '0.84rem',
                      fontWeight: isActive ? 600 : 500,
                      border: '1px solid',
                      borderColor: isActive ? 'var(--accent)' : 'var(--border)',
                      background: isActive ? 'var(--accent)' : 'var(--bg-glass)',
                      color: isActive ? '#ffffff' : 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? 'var(--shadow-glow)' : 'none',
                    }}
                  >
                    <span>{cat}</span>
                    <span
                      style={{
                        fontSize: '0.72rem',
                        padding: '0.1rem 0.45rem',
                        borderRadius: '100px',
                        background: isActive ? 'rgba(255, 255, 255, 0.25)' : 'rgba(45, 23, 83, 0.06)',
                        color: isActive ? '#ffffff' : 'var(--text-muted)',
                      }}
                    >
                      {count}
                    </span>
                  </button>
                </MagneticButton>
              );
            })}
          </div>

          {/* Search Input */}
          <div style={{ position: 'relative', minWidth: '240px' }}>
            <input
              type="text"
              placeholder="Search tech or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.55rem 1.1rem',
                borderRadius: '100px',
                border: '1px solid var(--border)',
                background: 'var(--bg-glass)',
                fontSize: '0.85rem',
                color: 'var(--text)',
                fontFamily: 'var(--font-body)',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-glow)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '0.85rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Projects Grid */}
        <motion.div
          layout
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
            gap: '2.25rem',
          }}
        >
          <AnimatePresence>
            {filteredProjects.map((project, i) => (
              <ProjectCard key={project.id} project={project} index={i} />
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredProjects.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '5rem 0',
              color: 'var(--text-muted)',
            }}
          >
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No projects match your search.</p>
            <button
              onClick={() => {
                setActiveCategory('All');
                setSearchQuery('');
              }}
              className="btn btn-outline"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
